import Header from "../components/header";
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import ContactForm from "../components/ContactForm";
import StarRating from "../components/StarRating";
import RichTextDisplay from "../components/RichTextDisplay";
import { useParams, Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import SimilarDoctorsCarousel from "../components/SimilarDoctorsCarousel";
import { doctorService } from "../services/api";
import { useAllDoctors } from "../hooks/useDoctors";

const DoctorDetails = () => {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { doctors: allDoctors } = useAllDoctors();

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to get doctor by ID from API first
        try {
          const response = await doctorService.getById(id);
          setDoctor(response.data);
        } catch (apiError) {
          // Fallback: search in all doctors array
          const selectedDoctor = allDoctors.find((doc) => doc.id === id);
          if (selectedDoctor) {
            setDoctor(selectedDoctor);
          } else {
            setError("Doctor not found");
          }
        }
      } catch (err) {
        setError("Failed to load doctor details");
        console.error("Error fetching doctor:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDoctor();
    }
    window.scrollTo(0, 0);
  }, [id, allDoctors]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-lg sm:text-xl text-blue-600 text-center">Loading doctor details...</div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen flex items-center justify-center text-lg sm:text-xl text-red-600 px-4 text-center">
        {error || "Doctor not found."}
      </div>
    );
  }

  return (
    <>
      <Header />
      <section className="bg-white py-6 sm:py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Top Section */}
          <div className="flex flex-col lg:flex-row items-start gap-6 sm:gap-8 mb-8 sm:mb-12">
            {/* Image Container */}
            <div className="lg:w-2/5 w-full">
              <img
                src={doctor.image}
                alt={doctor.name}
                className="w-full max-w-[280px] sm:max-w-[350px] h-[350px] sm:h-[450px] object-cover rounded-lg shadow-lg mx-auto lg:mx-0"
              />
            </div>

            {/* Info Container */}
            <div className="lg:w-3/5 w-full bg-white border border-gray-200 rounded-lg p-4 sm:p-8 shadow-sm">
              {/* Doctor Name */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-black mb-3 sm:mb-4 font-montserrat leading-tight">
                {doctor.name}
              </h1>

              {/* Star Rating */}
              {doctor.rating && (
                <div className="mb-4 sm:mb-6">
                  <StarRating 
                    rating={parseFloat(doctor.rating)} 
                    size="large" 
                    showRating={true}
                    className="justify-start"
                  />
                </div>
              )}

              {/* Specialty */}
              {doctor.specialty && (
                <div className="text-gray-600 text-lg sm:text-xl mb-4 sm:mb-6 font-open-sans">
                  {doctor.specialty}
                </div>
              )}

              {/* Hospital with icon */}
              {doctor.hospital && (
                <div className="flex items-center gap-2 sm:gap-3 text-gray-700 text-base sm:text-lg mb-3 sm:mb-4 font-open-sans">
                  <span className="text-blue-500 text-lg sm:text-xl">🏥</span>
                  <span className="break-words">{doctor.hospital}</span>
                </div>
              )}

              {/* Experience with icon */}
              <div className="flex items-center gap-2 sm:gap-3 text-gray-700 text-base sm:text-lg mb-6 sm:mb-8 font-open-sans">
                <span className="text-blue-500 text-lg sm:text-xl">💼</span>
                <span>{doctor.experience}</span>
              </div>

              {/* Book Appointment Button */}
              <div>
                <a
                  href="#contact-form"
                  className="bg-[#4285f4] hover:bg-[#3367d6] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium shadow-lg transition-all duration-200 inline-block font-montserrat w-full sm:w-auto text-center"
                >
                  Book An Appointment
                </a>
              </div>
            </div>
          </div>

          {/* Education and About */}
          <div className="mt-12 sm:mt-20 lg:mt-40">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 mb-16 sm:mb-28">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-black font-montserrat">
                  Qualifications
                </h2>
                {Array.isArray(doctor.qualifications) ? (
                  <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-1 font-open-sans text-sm sm:text-base">
                    {doctor.qualifications.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-700 font-open-sans text-sm sm:text-base">
                    {doctor.qualifications ||
                      "Qualification details not available."}
                  </p>
                )}

                <h2 className="text-xl sm:text-2xl font-bold mt-8 sm:mt-10 mb-3 sm:mb-4 text-black font-montserrat">
                  About the Doctor
                </h2>
                
                {/* Rich text formatted About section */}
                <RichTextDisplay 
                  content={doctor.about} 
                  className="text-gray-700 font-open-sans text-sm sm:text-base"
                />
                
                {/* Fallback if no rich content */}
                {!doctor.about && (
                  <p className="text-gray-700 font-open-sans text-sm sm:text-base">
                    No detailed info available yet.
                  </p>
                )}
              </div>

              {/* Contact Section */}
              <div>
                {/* Heading outside the box */}
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-black">
                    Get treated by your preferred doctor
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Start your treatment journey now
                  </p>
                </div>

                {/* Clean form */}
                <div id="contact-form">
                  <ContactForm formType="doctor-details-inquiry" />
                </div>
              </div>
            </div>
          </div>

          {/* Similar Doctors Carousel */}
          <SimilarDoctorsCarousel
            doctors={allDoctors.filter(
              (doc) =>
                doc.id !== doctor.id &&
                doc.specialty.trim().toLowerCase() ===
                  doctor.specialty.trim().toLowerCase()
            )}
            specialty={doctor.specialty}
            doctorName={doctor.name}
          />

          {/* CTA Section */}
          <section className="pt-8 sm:pt-16 pb-0">
        <div className="container mx-auto px-4">
          <div className="bg-[#4285f4] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center text-center lg:text-left">
              <div>
                <h4 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white leading-tight">
                  Get your treatment done by the best doctors of the world.
                </h4>
              </div>
              <div className="lg:text-right">
                <p className="text-white text-base sm:text-lg mb-4 sm:mb-6 font-medium">Get in touch with an Expert Advisor</p>
                <a 
                  href="https://wa.me/9599222655" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition inline-flex items-center justify-center text-base sm:text-lg shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  Chat Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
        </div>
      </section>
      <Footer />
      <FloatingChatButton />
    </>
  );
};

export default DoctorDetails;
